import { Component, DestroyRef, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { AuthService } from 'src/app/core/services/auth-security/auth.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { ParkerStateService } from 'src/app/core/services/state/parker-state.service';
import { AceStates } from 'src/app/shared/data/states';
import {
  ICompaniesShortList,
  ICurrentLotParkingAgreementVerbiage,
  ILanaiErrorResponse,
  ILotRateList,
  ILotUsageMetrics,
  IParkerProfileDetail,
  IParkingAgreementNewParkingAgreementLink,
  IRecentlyAddedParkerProfile,
  IStallTypes,
  IState,
  IAmanoParkerDataForCardReassignmentResponse,
} from 'src/app/shared/interfaces';
import { environment } from 'src/environments/environment';
import { ParkerImportPrep } from 'src/app/shared/helpers/profiles/parker-import-prep';
import { DialogSearchingComponent } from 'src/app/core/dialog/dialog-searching/dialog-searching.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CommonModule, CurrencyPipe, DatePipe, JsonPipe, NgStyle } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { YearSelectorDirectiveStandalone } from 'src/app/shared/directives/set-date-current-year-standalone.directive';
import { DialogConfirmNavigateAwayParkerComponent } from 'src/app/core/dialog/dialog-confirm-navigate-away-parker/dialog-confirm-navigate-away-parker.component';
import { DialogConfirmSaveParkerComponent } from '../parker-v2-dialogs/dialog-confirm-save-parker/dialog-confirm-save-parker.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DialogExemptionCommentsComponent } from '../parker-v2-dialogs/dialog-exemption-comments/dialog-exemption-comments.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { AmanoOneNewParkerComponent } from './parcs/amano-one-new-parker/amano-one-new-parker.component';
import {
  IAmanoOneAccessGroups,
  IParcsGetLotConfigurationResponse,
  IAmanoOneLotConfigurationSettings,
  IAmanoOneAccessHolderUpdateResponse,
} from 'src/app/shared/code/interfaces/parcs-interfaces';
import { ParcsOrganizations } from 'src/app/shared/code/constants-enums/lanai-enums';
import { ParcsTypeHelper } from 'src/app/shared/code/parcs-type.helper';
import { ParcsDataService } from 'src/app/core/services/data/parcs-data.service';
import { DialogAmanoParcsExistsNewParkerComponent } from 'src/app/core/dialog/dialog-amano-parcs-exists-new-parker/dialog-amano-parcs-exists-new-parker.component';
import { DialogAmanoParcsExistsNewParkerNoAddAccessHolderComponent } from 'src/app/core/dialog/dialog-amano-parcs-exists-new-parker-no-add-access-holder/dialog-amano-parcs-exists-new-parker-no-add-access-holder.component';
import { DialogCardNumberReassignComponent } from 'src/app/core/dialog/dialog-card-number-reassign/dialog-card-number-reassign.component';
import { ProcessingBannerComponent } from 'src/app/shared/ui/processing-banner/processing-banner.component';
import { FlashAddNewParkerComponent } from './parcs/flash-add-new-parker/flash-add-new-parker.component';
import {
  getParkerMaxEndDate,
  getParkerMaxEndDateDisplay,
  isParkerEndDateBeyondMax,
} from 'src/app/shared/code/parker-date-rules';


@Component({
  selector: 'app-parkers-v2-add-new-parker',
  standalone: true,
  imports: [
    MatIconModule,
    MatTabsModule,
    MatCardModule,
    ReactiveFormsModule,
    FormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatOptionModule,
    MatSelectModule,
    MatDatepickerModule,
    DatePipe,
    CurrencyPipe,
    NgStyle,
    CommonModule,
    MatButtonModule,
    YearSelectorDirectiveStandalone,
    MatCheckboxModule,
    AmanoOneNewParkerComponent,
    ProcessingBannerComponent,
    FlashAddNewParkerComponent,
    MatTooltipModule,
  ],
  templateUrl: './parkers-v2-add-new-parker.component.html',
  styleUrl: './parkers-v2-add-new-parker.component.scss',
})
export class ParkersV2AddNewParkerComponent implements OnInit, OnDestroy {
  // ==================== Template References ====================
  @ViewChild('cardNumberInput') cardNumberInput!: ElementRef<HTMLInputElement>;

  // ==================== UI / Form State ====================
  urlDomain: string = '';
  parkerNewParkerForm: FormGroup;
  currentCompanyId: string = '';
  disableSubmitNewParkerButton: boolean = true;
  companyList: ICompaniesShortList[] = [];
  marketRateList: ILotRateList[] = [];
  billingStates: IState[] = [];
  vehicleStates: IState[] = [];
  stallType: string = 'Select rate to view stall type';
  rateDescription: string = 'Select rate to view description';
  stallDescriptions: IStallTypes[] = [];
  newParkerId: string = '';
  addedParkerData: IParkerProfileDetail;
  saveAndAddNewParkerFlag: boolean = false;
  // 20260713 :: Idempotency token generated once per form fill; reused across double-clicks/retries so the API can dedupe duplicate submissions.
  submissionToken: string = '';
  gridFontSize: number = 12;
  rateDescriptionSummary: string = '';
  disableSaveButton: boolean = false;
  recentlyAddedParkers: IRecentlyAddedParkerProfile[] = [];
  currentlyActivePaymentRow: number = -1;
  isFormDirty: boolean = false;
  profileAdded: boolean = false;
  public screenWidth: any;
  public screenHeight: any;
  lotRateUsageMetrics: ILotUsageMetrics[] = [];
  labelSelectRate: string = 'Select Rate';
  sendNotification: boolean = true; //used to determine if to send a parking agreement when saving the new parker
  includeAgreementForm: boolean = false; //if true, identifies that when displaying a parking agreemnet web page to parker, it should collect address and vehicle information.  Used in another component application
  exemptAgreement: boolean = false;
  commentsForExemptAgreement: string = '';
  parkingCurrentActiveTemplateDetail: ICurrentLotParkingAgreementVerbiage = {} as ICurrentLotParkingAgreementVerbiage;
  parcsSaveButtonDisabled: boolean = false;
  vehicleLicenseInvalid: boolean = false;

  // Ceiling for the End Date, enforced by the datepicker and re-checked before submit.
  maxEndDate: Date = getParkerMaxEndDate();
  maxEndDateDisplay: string = getParkerMaxEndDateDisplay();
  isProcessingSave: boolean = false;
  processingMessage: string = 'Processing - Stand by...';
  credentialSystemTypeId: number = 0;
  credentialSystemTypeDescription: string = '';
  credentialSystemCodeId: string = '';
  credentialsDisabledForSave: boolean = true;
  cardNumber: string = '';

  // ==================== Card Reassignment State ====================
  isParcsProfileDisabled: boolean = false;
  isCardReassignmentPending: boolean = false;
  accessHolderIdForCardReassignment: string | null;

  // ==================== PARCS State ====================
  hasParc: boolean = false; // This parker needs to get PARC information during new Parker process
  parcsId: string = ''; // This is the PARC ID representing the PARCS used by this lot.
  parcsEnums = ParcsOrganizations;
  public get isParcsAmanoOne(): boolean {
    return ParcsTypeHelper.isAmanoOne(this.parcsId);
  }
  public get isParcsFlash(): boolean {
    return ParcsTypeHelper.isFlash(this.parcsId);
  }
  public amanoListConfigurations: IParcsGetLotConfigurationResponse = {} as IParcsGetLotConfigurationResponse;
  amanoOneConfigSettings: IAmanoOneLotConfigurationSettings = {} as IAmanoOneLotConfigurationSettings;
  amanoOneAccessGroupId: string;
  flashProfileId: string = '';
  flashProfileName: string = '';
  flashPoolName: string = '';
  createFlashAccount: boolean = true;

  isSpecialInstructionsEnabled: boolean = true;

  @HostListener('window:resize')
  onWindowResize() {
    this.screenWidth = window.innerWidth;
    this.screenHeight = window.innerHeight;
  }

  // ==================== Injected Services ====================
  private readonly _formBuilder = inject(FormBuilder);
  matSnackBar = inject(MatSnackBar);
  destroyRefService = inject(DestroyRef);
  _messageDialogService = inject(MessageDialogService);
  _parcsDataService = inject(ParcsDataService);
  _destroyRefService = inject(DestroyRef);
  _dialog = inject(MatDialog);
  _appGeneralState = inject(AppGeneralStateService);
  _parcDataService = inject(ParcsDataService);

  // ==================== Reactive Forms ====================
  readonly parkingAgreementForm = this._formBuilder.group({
    sendNotification: true,
    sendRequestForData: false,
    exemptFromAgreement: false,
    includeSpecialInstructionsLink: false,
  });

  /* 20240804 :: Ken Moss
*  Updated all lot numbers to use the _appGeneralState.currentLotNumber
   Removed class member lotNumber
*
*/

  // ==================== Lifecycle & Construction ====================
  constructor(
    private router: Router,
    private parkerStateService: ParkerStateService,
    private dataService: DataService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private snackBarService: MatSnackBar,
  ) {
    this._appGeneralState.canChangeLot = false;
    this.urlDomain = environment.UrlDomain;

    this.initNewParkerForm();
  }

  ngOnInit(): void {
    this.submissionToken = crypto.randomUUID(); // 20260713 :: New idempotency token for this new-parker form fill

    this.screenWidth = window.innerWidth;

    this.screenHeight = window.innerHeight;

    this.setParcsSetting();

    this.GetLotRateUsageMetrics(this._appGeneralState.currentLotNumber);

    this.initBillingStateList();

    this.initVehicleStateList();

    this.getStallDescriptionListForLotNumber(this._appGeneralState.currentLotNumber);

    this.checkIfFormIsDirty();

    this.getMarketRateListByLotNumber(this._appGeneralState.currentLotNumber);

    this.getParkingAgreementVerbigeForLot(this._appGeneralState.currentLotNumber);

    this.initWatchParkingAgreementCheckboxes();

    // Fetched for the current lot rather than read from ParkerStateService. That cache is written
    // asynchronously by other screens, so right after a lot change it still holds the previous
    // lot's companies - and a company from another lot fails the Parker/Company foreign key.
    // The lot cannot change while this screen is open (canChangeLot is false), so one fetch is enough.
    this.dataService
      .getCompanyListForLotNumber(this._appGeneralState.currentLotNumber)
      .pipe(takeUntilDestroyed(this.destroyRefService))
      .subscribe({
        next: (companyList) => (this.companyList = companyList),
        error: () => (this.companyList = []),
      });
  }

  ngOnDestroy(): void {
    this._appGeneralState.canChangeLot = true;
  }

  // ==================== UI Event Handlers (Child Outputs / Blur) ====================
  onBlurEmail(): void {
    let emailAddress: string = this.parkerNewParkerForm.get('primaryEmailAddress')!.value;

    if (emailAddress === '') {
      this.parkerNewParkerForm.patchValue({
        invoiceDeliveryMethod: 'Print',
      });
    }
  }

  onSetParcsSaveButtonState(enableStatus: boolean): void {
    this.parcsSaveButtonDisabled = enableStatus;
  }

  onSetParcsProfileDisabled(createAmanoOneProfile: boolean): void {
    this.isParcsProfileDisabled = !createAmanoOneProfile;
  }

  onSetAmanoOneDataInvalid(enableStatus: boolean): void {
    this.credentialsDisabledForSave = enableStatus;
  }

  onSetAmanoOneAccessGroupId(accessGroupId: string): void {
    this.amanoOneAccessGroupId = accessGroupId;
  }

  onSetAmanoOneCredentialTypeDescription(credentialTypeDescription: string): void {
    this.credentialSystemTypeDescription = credentialTypeDescription;
  }

  onSetAmanoOneCredentialTypeId(credentialTypeId: number): void {
    this.credentialSystemTypeId = credentialTypeId;
  }

  onSetAmanoOneCredentialSystemCodeId(credentialSystemCodeId: string): void {
    this.credentialSystemCodeId = credentialSystemCodeId;
  }

  onSetFlashProfileId(profileId: string): void {
    this.flashProfileId = profileId;
  }

  onSetFlashProfileName(profileName: string): void {
    this.flashProfileName = profileName;
  }

  onSetFlashPoolName(poolName: string): void {
    this.flashPoolName = poolName;
  }

  onSetCreateFlashAccount(createFlashAccount: boolean): void {
    this.createFlashAccount = createFlashAccount;
  }

  onSetFlashDataInvalid(isInvalid: boolean): void {
    this.credentialsDisabledForSave = isInvalid;
  }

  onBlurParkerPayType(): void {
    let parkerPayType: string = this.parkerNewParkerForm.get('parkerPayType')!.value;

    if (parkerPayType === 'Company') {
      this.parkerNewParkerForm.patchValue({
        invoiceDeliveryMethod: 'Print',
      });
    }
  }

  onValidateVehicleLicense(): void {
    const make = this.parkerNewParkerForm.get('vehicleMake')?.value ?? '';
    const model = this.parkerNewParkerForm.get('vehicleModel')?.value ?? '';
    const year = this.parkerNewParkerForm.get('vehicleYear')?.value ?? '';
    const state = this.parkerNewParkerForm.get('vehicleState')?.value ?? '';
    const color = this.parkerNewParkerForm.get('vehicleColor')?.value ?? '';
    const license = this.parkerNewParkerForm.get('vehicleLicense')?.value ?? '';

    const isOtherLicenseDataPresent: boolean =
      make.trim().length > 0 ||
      model.trim().length > 0 ||
      year.toString().trim().length > 0 ||
      state.trim().length > 0 ||
      color.trim().length > 0;

    const vehicleLicenseDataMissing: boolean = license.trim().length === 0;

    if (vehicleLicenseDataMissing && isOtherLicenseDataPresent) {
      this.vehicleLicenseInvalid = true;
      this.parkerNewParkerForm.controls['vehicleLicense'].setErrors({ vehicleLicense: true });
      this.parkerNewParkerForm.controls['vehicleModel'].setErrors({ vehicleModel: true });
      this.parkerNewParkerForm.controls['vehicleMake'].setErrors({ vehicleMake: true });
      this.parkerNewParkerForm.controls['vehicleState'].setErrors({ vehicleState: true });
      this.parkerNewParkerForm.controls['vehicleColor'].setErrors({ vehicleColor: true });
    } else {
      this.vehicleLicenseInvalid = false;
      this.parkerNewParkerForm.controls['vehicleLicense'].setErrors(null);
      this.parkerNewParkerForm.controls['vehicleModel'].setErrors(null);
      this.parkerNewParkerForm.controls['vehicleMake'].setErrors(null);
      this.parkerNewParkerForm.controls['vehicleState'].setErrors(null);
      this.parkerNewParkerForm.controls['vehicleColor'].setErrors(null);
    }
  }

  GetTextForLotRateUsageMetrics(rateNumber: number, rateType: string): string {
    let metricText: string = 'N/A';
    let ii: number = 0;
    let quantityUsed: number = 0;
    let limit: number = 0;

    try {
      for (ii = 0; ii < this.lotRateUsageMetrics.length; ii++) {
        if (
          this.lotRateUsageMetrics[ii].rateNumber == rateNumber &&
          this.lotRateUsageMetrics[ii].companyId == this.currentCompanyId &&
          this.lotRateUsageMetrics[ii].rateType == rateType
        ) {
          quantityUsed = this.lotRateUsageMetrics[ii].used;
          limit = this.lotRateUsageMetrics[ii].limit;
        }
      }

      metricText = quantityUsed.toLocaleString() + '/' + limit.toLocaleString();
    } catch (err) {
      console.error('Error: ' + JSON.stringify(err));
      metricText = 'N/A';
    }

    return metricText;
  }

  setParcsSetting(): void {
    this.hasParc = Object.keys(this._appGeneralState.currentLotParcsConfiguration).length !== 0;
    if (this.hasParc) {
      this.parcsId = this._appGeneralState.currentLotParcsConfiguration.parcsId.toUpperCase();
      const settings = this._appGeneralState.currentLotParcsConfiguration.settings;
      this.amanoOneConfigSettings = settings ? JSON.parse(settings) : {};
    } else {
      this.parcsSaveButtonDisabled = true;
    }

    // Only the Amano One child gates Submit on credential selection; every other lot starts enabled.
    // The Flash child re-raises this through onSetFlashDataInvalid once it knows whether a profile is selected.
    this.credentialsDisabledForSave = this.isParcsAmanoOne;
  }

  GetLimitsForLotMarketByCompanyId(): number {
    let limitNumber: number = 0;

    try {
    } catch (err) {
      limitNumber = 0;
    }

    return limitNumber;
  }

  GetLotUsageLimitForMarketRateByCompanyId(rateNumber: number, companyId: string) {}

  // ==================== Card Reassignment Flow ====================
  async onBlurCheckKeyCardIsValid(): Promise<void> {
    let cardNumber: string = this.parkerNewParkerForm.get('cardNumber')!.value;

    if (this.hasParc) {
      //let cardNumber: string = this.parkerNewParkerForm.get('cardNumber')!.value;
      // const accessHolderId: string = await this.getAmanoOneAccessHolderForCardNumberLotNumber(
      //   this._appGeneralState.currentLotNumber,
      //   cardNumber,
      // );
      // console.log('AccessHolderId', accessHolderId);
      // if (accessHolderId != '') {
      //   const amanoParkerData: IAmanoParkerDataForCardReassignmentResponse | null =
      //     await this.getAmanoOneParkerDataForCardReassignment(accessHolderId);
      //   if (amanoParkerData) {
      //     this.displayDialogCardNumberReassign(amanoParkerData);
      //   }
      // }
      this.cardNumber = cardNumber;
    } else {
      if (cardNumber.length > 0) {
        this.validateCardNumberAvailable(this._appGeneralState.currentLotNumber, cardNumber);
      } else {
        this.cardNumber = '';
      }
    }
  }

  // ==================== Data Loading / Lookup Lists ====================
  initBillingStateList(): void {
    let stateList = new AceStates();

    this.billingStates = stateList.AceUSStateList();
  }

  initVehicleStateList(): void {
    let stateList = new AceStates();

    this.vehicleStates = stateList.AceUSStateList();
  }

  getQSRRowBackgroundColor(rowNumber: number): string {
    if (this.currentlyActivePaymentRow === rowNumber) {
      return 'yellow';
    } else {
      if (rowNumber % 2 === 1) return '#e1e1e4';
      else return '#fff';
    }
  }

  onEvaluateStartEndDate(): void {
    let startDateEval: Date;
    let endDateEval: Date;

    startDateEval = this.parkerNewParkerForm.get('startDate')!.value;
    endDateEval = this.parkerNewParkerForm.get('endDate')!.value;

    if (endDateEval.toString() !== '') {
      const endDateControl = this.parkerNewParkerForm.controls['endDate'];

      if (startDateEval.toString() === '') {
        // Spread the existing errors so a datepicker max error set by [max] is not wiped out here.
        endDateControl.setErrors({ ...(endDateControl.errors ?? {}), endDate: true });
      } else {
        if (endDateEval < startDateEval) {
          endDateControl.setErrors({ ...(endDateControl.errors ?? {}), endDate: true });
        }
      }

      if (isParkerEndDateBeyondMax(endDateEval)) {
        endDateControl.setErrors({ ...(endDateControl.errors ?? {}), maxEndDate: true });
      }
    }
  }

  initNewParkerForm(): void {
    this.parkerNewParkerForm = new FormGroup({
      isActive: new FormControl('', []),
      companyId: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      firstName: new FormControl('', [Validators.required]),
      primaryEmailAddress: new FormControl('', []),
      startDate: new FormControl('', [Validators.required]),
      endDate: new FormControl('', []),
      cardNumber: new FormControl('', [Validators.maxLength(16)]),
      stallNumber: new FormControl('', []),
      rateNumber: new FormControl('', [Validators.required]),
      isTaxExempt: new FormControl('Select rate', []),
      parkerPayType: new FormControl('', [Validators.required]),
      invoiceDeliveryMethod: new FormControl('', [Validators.required]),
      billingAddress1: new FormControl('', []),
      billingAddressLine: new FormControl('', []),
      billingCity: new FormControl('', []),
      billingState: new FormControl('', []),
      billingZipCode: new FormControl('', []),
      vehicleModel: new FormControl('', []),
      vehicleMake: new FormControl('', []),
      vehicleColor: new FormControl('', []),
      vehicleState: new FormControl('', []),
      vehicleLicense: new FormControl('', []),
      vehicleEnforcementSubLot: new FormControl('', [
        // Up to 10 alphanumeric sub lots (1-6 chars each), separated by commas.
        // Spaces (ASCII 32) are allowed around the commas and at the start/end, e.g. "555A, 65778B".
        Validators.pattern(/^ *[A-Za-z0-9]{1,6}( *, *[A-Za-z0-9]{1,6}){0,9} *$/),
      ]),
      comments: new FormControl('', []),
      phone: new FormControl('', []),
      description: new FormControl('Select rate to view description', []),
      stallType: new FormControl('Select rate to view stall type', []),
    });
  }

  getParkingAgreementVerbigeForLot(lotNumber: string): void {
    let tempSettings: ICurrentLotParkingAgreementVerbiage = {} as ICurrentLotParkingAgreementVerbiage;

    this.dataService
      .GetParkingAgreementVerbiageForLot(lotNumber)
      .pipe(takeUntilDestroyed(this.destroyRefService))
      .subscribe({
        next: (results) => (tempSettings = results),
        error: (err) => {},
        complete: () => {
          this.parkingCurrentActiveTemplateDetail = tempSettings;
          this.parkingAgreementForm.patchValue({
            sendNotification: this.parkingCurrentActiveTemplateDetail.defaultValueSendParkingAgreement,
            sendRequestForData: this.parkingCurrentActiveTemplateDetail.defaultValueIncludeDataCollectionForm,
            includeSpecialInstructionsLink: this.parkingCurrentActiveTemplateDetail.includeSpecialInstructions,
          });
        },
      });
  }

  //
  validateCardNumberAvailable(lotNumber: string, cardNumber: string): void {
    let keyCardExists: boolean = false;
    let parkerId: string = 'new'; //This is required to make sure we validate a key card on a new profile

    cardNumber = cardNumber.trim();
    this.cardNumber = cardNumber;

    if (cardNumber === '0' || cardNumber === '') return;

    this.dataService.getKeyCardExistsByLotNumberCardNumber(lotNumber, cardNumber, parkerId).subscribe(
      (res) => {
        keyCardExists = res == true;
      },
      (err: any) => {},
      () => {
        if (keyCardExists) this.parkerNewParkerForm.controls['cardNumber'].setErrors({ cardInvalid: true });
        //  else
        //   this.parkerNewParkerForm.controls['cardNumber'].setErrors({'cardInvalid': false});
      },
    );
  }

  getCompanyAndMarketRateListByLotNumber(lotNumber: string, companyId: string) {
    this.dataService.getMarketCompanyRatesForLotAndCompany(lotNumber, companyId).subscribe(
      (res) => {
        this.marketRateList = res;
      },
      (err: any) => {
        this._dialog.closeAll();
        this.labelSelectRate = 'Select Rate';
      },
      () => {
        this._dialog.closeAll();
        this.labelSelectRate = 'Select Rate';
      },
    );
  }

  GetLotRateUsageMetrics(lotNumber: string) {
    this.dataService.GetLotMarketCompanyUsageMetrics(lotNumber).subscribe(
      (res) => {
        this.lotRateUsageMetrics = res;
      },
      (err: any) => {},
      () => {},
    );
  }

  getMarketRateListByLotNumber(lotNumber: string) {
    this.dataService.GetUniqueMarketRatesByLotNumber(lotNumber).subscribe(
      (res) => {
        this.marketRateList = res;
      },
      (err: any) => {
        this._dialog.closeAll();
        this.labelSelectRate = 'Select Rate';
      },
      () => {
        this._dialog.closeAll();
        this.labelSelectRate = 'Select Rate';
      },
    );
  }

  getStallDescriptionListForLotNumber(lotNumber: string) {
    this.dataService.getLotStallDescriptionList(lotNumber).subscribe(
      (res) => {
        this.stallDescriptions = res;
      },
      (err: any) => {},
      () => {},
    );
  }

  checkIfFormIsDirty() {
    this.parkerNewParkerForm.valueChanges.subscribe((res) => {
      this.isFormDirty = this.parkerNewParkerForm.dirty;
    });
  }

  // ==================== Navigation & Submit Entry Points ====================
  onRouteToParkersHome(): void {
    if (this.isFormDirty) {
      const dialogRef = this._dialog.open(DialogConfirmNavigateAwayParkerComponent);
      dialogRef.afterClosed().subscribe((result) => {
        if (result) this.parkerStateService.modeAddNew = false;
      });
    } else this.parkerStateService.modeAddNew = false;
  }

  onSubmitNewParkerProfileAndAddAnother(): void {
    let newParkerData: any = this.setAddParkerModel();
    const agreementChecked: boolean = Boolean(this.parkingAgreementForm.get('sendNotification')!.value);
    this.saveAndAddNewParkerFlag = true;
    if (newParkerData.emailAddress == '' && agreementChecked) {
      this.displayDialogNoEmailAddress();
    } else {
      this.saveAndAddNewParkerFlag = true;
      this.addNewParkerProfileAndAddAnother();
    }
  }

  displayAddingParkerMessage(): void {
    const verticalPosition: MatSnackBarVerticalPosition = 'top';

    this.snackBarService.open('Stand by while the new Parker is being added...', '', {
      verticalPosition: verticalPosition,
    });
  }

  closeDisplayAddingParkerMessage(): void {
    this.snackBarService.dismiss();
  }

  onSubmitNewParkerProfile(): void {
    let newParkerData: any = this.setAddParkerModel();
    const agreementChecked: boolean = Boolean(this.parkingAgreementForm.get('sendNotification')!.value);
    this.saveAndAddNewParkerFlag = false;
    if (newParkerData.emailAddress == '' && agreementChecked) {
      this.displayDialogNoEmailAddress();
    } else {
      this.addNewParkerProfile();
    }
  }

  displayDialogNoEmailAddress(): void {
    const enterAnimationDuration: string = '0ms';
    const exitAnimationDuration: string = '0ms';
    const agreementChecked: boolean = Boolean(this.parkingAgreementForm.get('sendNotification')!.value);

    const dialogRef = this._dialog.open(DialogConfirmSaveParkerComponent, {
      data: { isAgreementChecked: agreementChecked },
      width: '450px',
      enterAnimationDuration,
      exitAnimationDuration,
    });

    dialogRef.afterClosed().subscribe((result) => {
      // This dialog is for display with no actions after close
    });
  }

  evalSendMonthlyParkingAgreement(): void {}

  /// <summary>
  /// Evaluate if the Amano One Parcs card number already exists
  /// </summary>
  // async evalIfAmanoOneParcsCardNumberExists(newParkerData: any, accessHolderId: string | null): Promise<void> {
  //   // Determine if the card number is already being used in an Amano One access holder profile
  //   // If the card number already exists, then display a dialog with the following choices:
  //   // 1) Synch the new Parker profile to the existing Amano One access holder profile
  //   // 2) Cancel the save operation and return to the new Parker profile

  //   if (accessHolderId == null || accessHolderId == '') {
  //     this.disableSaveButton = true;
  //     this.postNewParkerProfileAmanoOne(newParkerData);
  //   } else {
  //     if (!this.parcsSaveButtonDisabled) {
  //       // if we are adding the parcs on saving the parker
  //       // Get the Access Holder Data
  //       let accessHolder: IAmanoOneAccessHolderUpdateResponse | null = await this.getAmanoOneAccessHolderTrackingData(
  //         newParkerData.lotNumber,
  //         newParkerData.cardNumber,
  //       );
  //       this.displayAmanoParcsExistsDialog(newParkerData, accessHolder);
  //     } else {
  //     }
  //   }
  // }

  // ==================== PARCS Access Holder Dialogs ====================
  displayAmanoParcsExistsDialogNotAddingAccessHolder(
    newParkerData: any,
    accessHolder: IAmanoOneAccessHolderUpdateResponse | null,
  ): void {
    // const dlgTitle: string = 'Delete Invoice?';
    // const dlgLine1: string = `You are about to delete a payment associated with invoice number .  This is PERMANENT AND CANNOT BE REVERSED.`;
    // const dlgLine2: string = `Proceed?`;
    // let spaceUserId: number;

    let enterAnimationDuration: string = '0ms';
    let exitAnimationDuration: string = '0ms';

    const dialogRef = this._dialog.open(DialogAmanoParcsExistsNewParkerNoAddAccessHolderComponent, {
      data: { accessHolder: accessHolder },
      hasBackdrop: true,
      width: '85vw',
      maxWidth: '90vh',
      panelClass: 'custom-dialog-container',
      enterAnimationDuration,
      exitAnimationDuration,
      position: {
        top: '200px',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      // if (result) {
      //   this.onUpdateExistingAmanoOneParcsAccessHolder(newParkerData, accessHolder.accessHolderID);
      // } else {
      //   const input = this.cardNumberInput.nativeElement;
      //   input.focus();
      //   input.select();
      //   this.disableSaveButton = false;
      // }
    });
  }

  displayAmanoParcsExistsDialog(newParkerData: any, accessHolder: IAmanoOneAccessHolderUpdateResponse): void {
    // const dlgTitle: string = 'Delete Invoice?';
    // const dlgLine1: string = `You are about to delete a payment associated with invoice number .  This is PERMANENT AND CANNOT BE REVERSED.`;
    // const dlgLine2: string = `Proceed?`;
    // let spaceUserId: number;

    let enterAnimationDuration: string = '0ms';
    let exitAnimationDuration: string = '0ms';

    const dialogRef = this._dialog.open(DialogAmanoParcsExistsNewParkerComponent, {
      data: { accessHolder: accessHolder },
      hasBackdrop: true,
      width: '85vw',
      maxWidth: '90vh',
      panelClass: 'custom-dialog-container',
      enterAnimationDuration,
      exitAnimationDuration,
      position: {
        top: '200px',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.onUpdateExistingAmanoOneParcsAccessHolder(newParkerData, accessHolder.accessHolderID);
      } else {
        const input = this.cardNumberInput.nativeElement;
        input.focus();
        input.select();
        this.disableSaveButton = false;
      }
    });
  }

  onUpdateExistingAmanoOneParcsAccessHolder(newParkerData: any, accessHolderId: string): void {
    this._parcsDataService.updateAmanoOneParcsAccessHolderDuringAddNewParker(newParkerData, accessHolderId).subscribe(
      (res) => {
        if (res) {
          this.postNewParkerProfileAmanoOne(newParkerData);
          //this._messageDialogService.snackBarTopCenterTimed('Parker profile updated successfully', 5000);
        }
      },
      (err: any) => {
        this._messageDialogService.snackBarTopCenterTimed(
          'An unexpected error occurred updating the Parker profile',
          5000,
        );
      },
    );
  }

  // ==================== Save / Persistence Flow ====================
  async addNewParkerProfileAndAddAnother(): Promise<void> {
    this.saveAndAddNewParkerFlag = true;

    let newParkerData: any = this.setAddParkerModel();

    this.parkerStateService.activeParkerId = this.newParkerId;

    this.addMostRecentSavedParkerToRecentParkerList(newParkerData);

    if (this.hasParc && this.isParcsAmanoOne) {
      this.disableSaveButton = true;
      this.postNewParkerProfileAmanoOne(newParkerData);

      /* * * * * * * * *
      * 20260618 :: Ken Moss
      * Removed but left in via commented out in the event we want to reinstate this logic.
      * * * * * * * * * *
      if (newParkerData.cardNumber && newParkerData.cardNumber != '') {
        const accessHolderId = await this.getAmanoOneAccessHolderId(newParkerData.lotNumber, newParkerData.cardNumber);
        this.evalIfAmanoOneParcsCardNumberExists(newParkerData, accessHolderId);
      } else {
        this.disableSaveButton = true;
        this.postNewParkerProfileAmanoOne(newParkerData);
      }

      = = = = = = = = = =
      = 20260618 :: Ken Moss
      = Removed but left in via commented out in the event we want to reinstate this logic.
      = = = = = = = = = = */
    } else if (this.hasParc && this.isParcsFlash) {
      this.disableSaveButton = true;
      this.postNewParkerProfileFlash(newParkerData);
    } else {
      this.disableSaveButton = true;
      this.postNewParkerProfile(newParkerData);
    }
  }

  async evaluateIfAmanoOneParcsCardNumberExists(newParkerData: any): Promise<void> {
    this.accessHolderIdForCardReassignment = ''; // Reset accessHolderId;
    let cardNumber: string = this.parkerNewParkerForm.get('cardNumber')!.value;
    const accessHolderId: string | null = await this.getAmanoOneAccessHolderForCardNumberLotNumber(
      this._appGeneralState.currentLotNumber,
      cardNumber,
    );
    console.log('AccessHolderId', accessHolderId);
    if (accessHolderId != '') {
      this.accessHolderIdForCardReassignment = accessHolderId;
      const amanoParkerData: IAmanoParkerDataForCardReassignmentResponse | null =
        await this.getAmanoOneParkerDataForCardReassignment(accessHolderId);
      if (amanoParkerData) {
        const confirmed: boolean = await this.displayDialogCardNumberReassign(amanoParkerData);
        if (confirmed) {
        }
      }
    }
  }

  async addNewParkerProfile(): Promise<void> {
    let newParkerData: any = this.setAddParkerModel();
    if (this.hasParc && this.isParcsAmanoOne) {
      this.evaluateIfAmanoOneParcsCardNumberExists(newParkerData);
    } else {
      //this.saveParkerProfile();
      console.log('where non parcs save will occur');
    }
  }

  async saveParkerProfile(): Promise<void> {
    this.saveAndAddNewParkerFlag = false;

    let newParkerData: any = this.setAddParkerModel();
    this.parkerStateService.activeParkerId = this.newParkerId;

    if (this.hasParc && this.isParcsAmanoOne && this.parcsSaveButtonDisabled == false && this.amanoOneAccessGroupId != '') {
      this.disableSaveButton = true;
      this.postNewParkerProfileAmanoOne(newParkerData);

      /* * * * * * * * *
      * 20260618 :: Ken Moss
      * Removed but left in via commented out in the event we want to reinstate this logic.
      * * * * * * * * * *
      // if PARCS enabled and we are adding an AccessHolder profile
      // PARCS Path
      if (newParkerData.cardNumber && newParkerData.cardNumber != '') {
        const accessHolderId = await this.getAmanoOneAccessHolderId(newParkerData.lotNumber, newParkerData.cardNumber);
        this.evalIfAmanoOneParcsCardNumberExists(newParkerData, accessHolderId);
      } else {
        this.disableSaveButton = true;
        this.postNewParkerProfileAmanoOne(newParkerData);
      }

      = = = = = = = = = = 
      = 20260618 :: Ken Moss
      = Removed but left in via commented out in the event we want to reinstate this logic.
      = = = = = = = = = */
    } else if (this.hasParc && this.isParcsFlash && this.parcsSaveButtonDisabled == false) {
      this.disableSaveButton = true;
      this.postNewParkerProfileFlash(newParkerData);
    } else if (this.hasParc && this.isParcsAmanoOne && this.parcsSaveButtonDisabled == false && this.amanoOneAccessGroupId == '') {
      // if PARCS enabled and we are NOTadding an AccessHolder profile
      const accessHolderId = await this.getAmanoOneAccessHolderId(newParkerData.lotNumber, newParkerData.cardNumber);
      if (accessHolderId != null && accessHolderId != '') {
        let accessHolder: IAmanoOneAccessHolderUpdateResponse | null = await this.getAmanoOneAccessHolderTrackingData(
          newParkerData.lotNumber,
          newParkerData.cardNumber,
        );
        this.displayAmanoParcsExistsDialogNotAddingAccessHolder(newParkerData, accessHolder);
      } else {
        this.disableSaveButton = true;
        this.postNewParkerProfile(newParkerData);
      }
    } else {
      // No PARCS Path
      this.disableSaveButton = true;
      this.postNewParkerProfile(newParkerData);
    }
  }

  addMostRecentSavedParkerToRecentParkerList(newlyAddedParker: IParkerProfileDetail): void {
    let newParker: IRecentlyAddedParkerProfile = {} as IRecentlyAddedParkerProfile;

    let ii: number;
    let companyName: string = '';

    for (ii = 0; ii < this.companyList.length; ii++) {
      if (this.companyList[ii].companyId === newlyAddedParker.companyId) companyName = this.companyList[ii].companyName;
    }

    newParker.emailAddress = newlyAddedParker.emailAddress;
    newParker.companyName = companyName;
    newParker.firstName = newlyAddedParker.firstName;
    newParker.lastName = newlyAddedParker.lastName;
    newParker.startDate = newlyAddedParker.startDate;
    newParker.endDate = newlyAddedParker.endDate;
    newParker.parkerPayType = newlyAddedParker.parkerPayType;
    newParker.invoiceDeliveryMethod = newlyAddedParker.deliveryType;

    this.recentlyAddedParkers.push(newParker);
  }

  // ==================== HTTP POST Calls ====================
  postNewParkerProfile(parkerProfileData: IParkerProfileDetail) {
    let postStatus: boolean;

    parkerProfileData.parcsType = ''; // no PARCS for this parker

    this.isProcessingSave = true;
    this.processingMessage = 'Saving New Parker - Stand by...';

    this.dataService.postNewParkerProfile(parkerProfileData).subscribe(
      (res) => {
        postStatus = res.success;
        const successMessage = res.warningMessage
          ? `Parker profile added successfully. ${res.warningMessage}`
          : 'Parker profile added successfully';
        this._messageDialogService.snackBarTopCenterTimed(successMessage, 5000);
      },
      (err: any) => {
        this.isProcessingSave = false;
        this.disableSaveButton = false; // Re-enable so the user can retry after a failed save

        console.error('Error: ' + JSON.stringify(err));

        // The API returns an ILanaiErrorResponse body ({errorSource, errorMessageLine1, errorMessageLine2, ...}).
        // Fall back to a generic message rather than stringifying the raw object (which renders as "[object Object]").
        const errResponse: ILanaiErrorResponse | undefined = err?.error;
        const errorSource = errResponse?.errorSource ?? 'spACE-AR General Error';
        const errorLine1 = errResponse?.errorMessageLine1 ?? 'Failed to save Parker Profile';
        const errorLine2 =
          errResponse?.errorMessageLine2 ??
          (typeof err?.error === 'string' ? err.error : 'An unknown error occurred. Please try again.');

        this._messageDialogService.displayGenericErrorDialog(errorSource, errorLine1, errorLine2);
      },
      () => {
        this.isProcessingSave = false;
        this.parkerStateService.setRemoveParkerProfileQueryResults(true);
        this.setAddedParkerData(parkerProfileData);
        if (this.saveAndAddNewParkerFlag == false) {
          //This path navigates back to the Parker Search List after saving
          this.onAfterPostParkerProfile(parkerProfileData.parkerId);
          this.parkerStateService.modeAddNew = false;
        } else {
          //This path will let the user add multiple parkers
          this.disableSaveButton = false;
          this.resetParkerAndPrepareForNewParker();
          this.onAfterPostParkerProfile(parkerProfileData.parkerId);
          this.evalSendMonthlyParkingAgreement();
        }
      },
    );
  }

  postNewParkerProfileAmanoOne(parkerProfileData: IParkerProfileDetail) {
    let postStatus: boolean;
    if (this.credentialSystemCodeId == '') {
      this.credentialSystemCodeId = '-1';
    }

    if (this.credentialSystemTypeId == 0) {
      this.credentialSystemTypeId = -1;
    }

    // Move the Amano route params onto the payload for the unified AddNewParkerProfile endpoint
    parkerProfileData.parcsType = 'AmanoOne';
    parkerProfileData.amanoOne = {
      accessGroupId: this.amanoOneAccessGroupId,
      credentialTypeId: this.credentialSystemTypeId.toString(),
      credentialSystemCode: this.credentialSystemCodeId,
      cardReassign: this.isCardReassignmentPending,
    };

    this.isProcessingSave = true;
    this.processingMessage = 'Saving New Parker with Amano One - Stand by...';
    let agreementWarningMessage: string | null = null;
    this.dataService.postNewParkerProfile(parkerProfileData).subscribe(
      (res) => {
        postStatus = res.success;
        agreementWarningMessage = res.warningMessage ?? null;
        const successMessage = agreementWarningMessage
          ? `Parker profile added successfully. ${agreementWarningMessage}`
          : 'Parker profile added successfully';
        this._messageDialogService.snackBarTopCenterTimed(successMessage, 5000);
      },
      (err: any) => {
        this.isProcessingSave = false;
        this.disableSaveButton = false; // Re-enable so the user can retry after a failed save
        let errorNumberMessage: string = '';

        const errMessageResponse: ILanaiErrorResponse = err.error;
        console.error('Error: ', errMessageResponse);

        if (err.error != null || undefined) {
          this._messageDialogService.displayGenericErrorDialog(
            `${errMessageResponse.errorSource} System Error`,
            errMessageResponse.errorMessageLine1,
            errMessageResponse.errorMessageLine2,
          );
        }
      },
      () => {
        this.isProcessingSave = false;
        this.parkerStateService.setRemoveParkerProfileQueryResults(true);

        this.setAddedParkerData(parkerProfileData);
        if (this.saveAndAddNewParkerFlag == false) {
          //This path navigates back to the Parker Search List after saving
          this.onAfterPostParkerProfile(parkerProfileData.parkerId);
          this._messageDialogService.snackBarTopCenterTimed(
            agreementWarningMessage
              ? `The Parker has been succesfully added. ${agreementWarningMessage}`
              : 'The Parker has been succesfully added.',
            3000,
          );
          this.parkerStateService.modeAddNew = false;
        } else {
          //This path will let the user add multiple parkers
          this._messageDialogService.snackBarTopCenterTimed(
            agreementWarningMessage
              ? `The Parker has been succesfully added. ${agreementWarningMessage} Stand by to add another Parker.`
              : 'The Parker has been succesfully added. Stand by to add another Parker.',
            3000,
          );
          this.disableSaveButton = false;
          this.resetParkerAndPrepareForNewParker();
          this.onAfterPostParkerProfile(parkerProfileData.parkerId);
          this.evalSendMonthlyParkingAgreement();
        }
      },
    );
  }

  postNewParkerProfileFlash(parkerProfileData: IParkerProfileDetail) {
    parkerProfileData.parcsType = 'Flash';
    // Passback is not offered on the add form; it is set afterwards from the PARCS tab.
    parkerProfileData.flash = {
      profileId: this.flashProfileId,
      profileName: this.flashProfileName,
      poolName: this.flashPoolName,
      createAccount: this.createFlashAccount,
      allowPassback: false,
    };

    this.isProcessingSave = true;
    this.processingMessage = 'Saving New Parker with Flash - Stand by...';
    let agreementWarningMessage: string | null = null;

    this.dataService.postNewParkerProfile(parkerProfileData).subscribe(
      (res) => {
        agreementWarningMessage = res.warningMessage ?? null;
      },
      (err: any) => {
        this.isProcessingSave = false;
        this.disableSaveButton = false; // Re-enable so the user can retry after a failed save

        const errMessageResponse: ILanaiErrorResponse | undefined = err?.error;
        console.error('Error: ', errMessageResponse);

        this._messageDialogService.displayGenericErrorDialog(
          `${errMessageResponse?.errorSource ?? 'spACE-AR'} System Error`,
          errMessageResponse?.errorMessageLine1 ?? 'Failed to save Parker Profile',
          errMessageResponse?.errorMessageLine2 ?? 'An unknown error occurred. Please try again.',
        );
      },
      () => {
        this.isProcessingSave = false;
        this.parkerStateService.setRemoveParkerProfileQueryResults(true);
        this.setAddedParkerData(parkerProfileData);

        if (this.saveAndAddNewParkerFlag == false) {
          //This path navigates back to the Parker Search List after saving
          this.onAfterPostParkerProfile(parkerProfileData.parkerId);
          this._messageDialogService.snackBarTopCenterTimed(
            agreementWarningMessage
              ? `The Parker has been succesfully added. ${agreementWarningMessage}`
              : 'The Parker has been succesfully added.',
            3000,
          );
          this.parkerStateService.modeAddNew = false;
        } else {
          //This path will let the user add multiple parkers
          this._messageDialogService.snackBarTopCenterTimed(
            agreementWarningMessage
              ? `The Parker has been succesfully added. ${agreementWarningMessage} Stand by to add another Parker.`
              : 'The Parker has been succesfully added. Stand by to add another Parker.',
            3000,
          );
          this.disableSaveButton = false;
          this.resetParkerAndPrepareForNewParker();
          this.onAfterPostParkerProfile(parkerProfileData.parkerId);
          this.evalSendMonthlyParkingAgreement();
        }
      },
    );
  }

  // ==================== Post-Save Handling ====================
  showSnackbarMessage(): void {
    let action: any;
    this.snackBarService.open('The new Parker Profile have been saved.', action, {
      duration: 5000,
    });
  }

  onAfterPostParkerProfile(parkerDetailId: string): void {
    let mostRecentAddedParker: IParkerProfileDetail = {} as IParkerProfileDetail;
    this.dataService.getParkerDetailByParkerId(parkerDetailId).subscribe(
      (res) => {
        mostRecentAddedParker = res;
      },
      (err: any) => {},
      () => {
        let parkerQueryResults: IParkerProfileDetail[] = [];

        parkerQueryResults.push(mostRecentAddedParker);

        this.parkerStateService.parkerQueryResults = parkerQueryResults;
        this.parkerStateService.filteredParkers = parkerQueryResults;
      },
    );
  }

  resetParkerAndPrepareForNewParker(): void {
    this.initNewParkerForm();
    this.submissionToken = crypto.randomUUID(); // 20260713 :: New idempotency token for the next parker ("Add Another")
  }

  // ==================== Model Mapping ====================
  setAddParkerModel(): any {
    let newParkerData: IParkerProfileDetail = {} as IParkerProfileDetail;
    let endDateNotSetFlag: Date = new Date('1900-01-01');
    let parkerHelper = new ParkerImportPrep(this.companyList);
    let parkerAgreementData: IParkingAgreementNewParkingAgreementLink = {} as IParkingAgreementNewParkingAgreementLink;

    this.newParkerId = parkerHelper.CreateNewParkerId();
    newParkerData.parkerId = this.newParkerId;
    newParkerData.submissionToken = this.submissionToken; // 20260713 :: Reuse the per-form-fill token (do NOT regenerate here) so retries/double-clicks dedupe server-side
    newParkerData.lotNumber = this._appGeneralState.currentLotNumber;
    newParkerData.companyId = this.parkerNewParkerForm.get('companyId')!.value;
    newParkerData.lastName = this.parkerNewParkerForm.get('lastName')!.value;
    newParkerData.firstName = this.parkerNewParkerForm.get('firstName')!.value;
    newParkerData.address = this.parkerNewParkerForm.get('billingAddress1')!.value;
    newParkerData.addressLine = this.parkerNewParkerForm.get('billingAddressLine')!.value;
    newParkerData.city = this.parkerNewParkerForm.get('billingCity')!.value;
    newParkerData.state = this.parkerNewParkerForm.get('billingState')!.value;
    newParkerData.zipCode = this.parkerNewParkerForm.get('billingZipCode')!.value;
    newParkerData.phone = this.parkerNewParkerForm.get('phone')!.value;
    newParkerData.emailAddress = this.parkerNewParkerForm.get('primaryEmailAddress')!.value;
    newParkerData.activeAsBoolean = true;
    newParkerData.cardNumber = this.parkerNewParkerForm.get('cardNumber')!.value;
    newParkerData.vehicleColor = this.parkerNewParkerForm.get('vehicleColor')!.value;
    newParkerData.vehicleMake = this.parkerNewParkerForm.get('vehicleMake')!.value;
    newParkerData.vehicleModel = this.parkerNewParkerForm.get('vehicleModel')!.value;
    newParkerData.vehicleLicense = this.parkerNewParkerForm.get('vehicleLicense')!.value;
    newParkerData.vehicleState = this.parkerNewParkerForm.get('vehicleState')!.value;
    newParkerData.vehicleEnforcementSubLot = this.parkerNewParkerForm.get('vehicleEnforcementSubLot')!.value;
    newParkerData.payType = 'todo - get pay type';
    newParkerData.rateNumber = +this.parkerNewParkerForm.get('rateNumber')!.value;
    newParkerData.deliveryType = this.parkerNewParkerForm.get('invoiceDeliveryMethod')!.value;
    newParkerData.parkerPayType = this.parkerNewParkerForm.get('parkerPayType')!.value;
    newParkerData.startDate = this.parkerNewParkerForm.get('startDate')!.value;
    newParkerData.endDate = this.parkerNewParkerForm.get('endDate')!.value;
    newParkerData.userName = this._appGeneralState.userName;
    newParkerData.stallNumber = this.parkerNewParkerForm.get('stallNumber')!.value;
    newParkerData.isTermDateSet = true;
    newParkerData.comments = this.parkerNewParkerForm.get('comments')!.value;
    newParkerData.group = '';
    newParkerData.active = '';
    newParkerData.company = '';
    newParkerData.stallType = this.parkerNewParkerForm.get('stallType')!.value;
    newParkerData.companyName = '';
    newParkerData.paymentType = '';
    newParkerData.rateDisplay = '';
    //newParkerData.addressLine = '';
    newParkerData.endDateDisplay = '';
    newParkerData.rateDescription = '';
    newParkerData.additionalEmails = 0;
    newParkerData.companyStartDate = '';
    newParkerData.standardRateType = '';
    newParkerData.startDateDisplay = '';
    newParkerData.additionalVehicles = 0;
    newParkerData.rateNumberAsString = '';
    newParkerData.additionalCardCount = '';
    newParkerData.additionalEmailCount = '';
    newParkerData.rateDescriptionSummary = '';
    newParkerData.rateType = '';
    newParkerData.spaceEmailAddress = '';
    newParkerData.spaceEmailId = 0;
    newParkerData.lotName = '';
    newParkerData.parkingAgreementNotification = parkerAgreementData;

    if (newParkerData.parkerPayType.toUpperCase() === 'PARKER') newParkerData.tenantId = this.newParkerId;
    else newParkerData.tenantId = newParkerData.companyId;

    //If the user didn't select an end date, use this as a flag with the endpoint
    if (
      this.parkerNewParkerForm.get('endDate')!.value === '' ||
      this.parkerNewParkerForm.get('endDate')!.value === null
    )
      newParkerData.endDate = endDateNotSetFlag;

    // 20240809 :: Ken Moss.  Fixed below.  If statement was incorrect and not setting tax exempt flag correctly
    // 20241007 :: Ken Moss.  Removed the else portion of this if statement, which was incorrect.  The else was setting the taxExempt = true
    const taxExemptFlag: string = this.parkerNewParkerForm.get('isTaxExempt')!.value;
    if (taxExemptFlag === '') {
      newParkerData.taxExempt = false;
    } else {
      newParkerData.taxExempt = taxExemptFlag.toUpperCase() == 'YES' ? true : false;
    }

    if (this.parkingAgreementForm.get('sendNotification')!.value) {
      parkerAgreementData.includeDataRequest = this.parkingAgreementForm.get('sendRequestForData')!.value;
      parkerAgreementData.parkerId = this.newParkerId;
      parkerAgreementData.lotNumber = this._appGeneralState.currentLotNumber;
      parkerAgreementData.userName = this._appGeneralState.userName;
      parkerAgreementData.includeSpecialInstructions = this.parkingAgreementForm.get(
        'includeSpecialInstructionsLink',
      )!.value;
      parkerAgreementData.isExempt = false;
      parkerAgreementData.exemptComments = '';
      parkerAgreementData.isResendNotification = false;
      parkerAgreementData.isNewAgreement = true;

      // 20241011 :: Ken Moss.  The email address was added to the parkerAgreementData model
      parkerAgreementData.recipientEmailAddress = this.parkerNewParkerForm.get('primaryEmailAddress')!.value;
      newParkerData.parkingAgreementNotification = parkerAgreementData;
    }

    if (this.parkingAgreementForm.get('exemptFromAgreement')!.value == true) {
      parkerAgreementData.isExempt = true;
      parkerAgreementData.parkerId = this.newParkerId;
      parkerAgreementData.exemptComments = this.commentsForExemptAgreement;
      parkerAgreementData.isNewAgreement = false;

      newParkerData.parkingAgreementNotification = parkerAgreementData;
    }

    return newParkerData;
  }

  setAddedParkerData(newParkerData: IParkerProfileDetail): void {
    let parkerData: IParkerProfileDetail = {} as IParkerProfileDetail;

    let aStallType: IStallTypes;

    let company: ICompaniesShortList;
    company = this.companyList.find((x) => x.companyId == newParkerData.companyId)!;

    aStallType = this.stallDescriptions.find((x) => x.rateNumber == newParkerData.rateNumber)!;

    parkerData.parkerId = newParkerData.parkerId;
    parkerData.lotNumber = newParkerData.lotNumber;
    parkerData.companyId = newParkerData.companyId;
    parkerData.lastName = newParkerData.lastName;
    parkerData.firstName = newParkerData.firstName;
    parkerData.address = newParkerData.address;
    parkerData.addressLine = newParkerData.addressLine;
    parkerData.city = newParkerData.city;
    parkerData.state = newParkerData.state;
    parkerData.zipCode = newParkerData.zipCode;
    parkerData.phone = newParkerData.phone;
    parkerData.emailAddress = newParkerData.emailAddress;
    parkerData.active = newParkerData.active;
    parkerData.cardNumber = newParkerData.cardNumber;
    parkerData.vehicleColor = newParkerData.vehicleColor;
    parkerData.vehicleMake = newParkerData.vehicleMake;
    parkerData.vehicleModel = newParkerData.vehicleModel;
    parkerData.vehicleLicense = newParkerData.vehicleLicense;
    parkerData.vehicleState = newParkerData.vehicleState;
    parkerData.payType = newParkerData.payType;
    parkerData.rateNumber = +newParkerData.rateNumber;
    parkerData.deliveryType = newParkerData.deliveryType;
    parkerData.parkerPayType = newParkerData.parkerPayType;
    parkerData.startDate = newParkerData.startDate;
    parkerData.userName = newParkerData.userName;
    parkerData.stallNumber = newParkerData.stallNumber;
    parkerData.isTermDateSet = newParkerData.isTermDateSet;
    parkerData.comments = newParkerData.comments;
    parkerData.tenantId = newParkerData.tenantId;
    parkerData.companyName = company.companyName;
    parkerData.rateDescription = aStallType.description;
    parkerData.stallType = aStallType.parkingStallType;
    parkerData.rateDescriptionSummary = this.rateDescriptionSummary;
    parkerData.group = '';
    parkerData.active = '';
    parkerData.company = '';
    parkerData.stallType = '';
    parkerData.paymentType = '';
    parkerData.rateDisplay = '';
    parkerData.addressLine = '';
    parkerData.endDateDisplay = newParkerData.endDate.toDateString();
    parkerData.rateDescription = '';
    parkerData.additionalEmails = 0;
    parkerData.companyStartDate = '';
    parkerData.standardRateType = '';

    if (newParkerData.endDate.toDateString() === 'Sun Dec 31 1899') {
      parkerData.endDateDisplay = '0001-01-01T00:00:00';
      parkerData.endDate = new Date('0001-01-01T00:00:00');
    } else {
      parkerData.endDateDisplay = newParkerData.endDate.toDateString();
      parkerData.endDate = newParkerData.endDate;
    }

    parkerData.startDateDisplay = newParkerData.startDate.toDateString();

    parkerData.additionalVehicles = 0;
    parkerData.rateNumberAsString = '';
    parkerData.additionalCardCount = '';
    parkerData.additionalEmailCount = '';
    parkerData.rateDescriptionSummary = '';
    parkerData.rateType = '';
    parkerData.spaceEmailAddress = '';

    this.addedParkerData = parkerData;
    this.parkerStateService.setParkerDetail(parkerData);
  }

  // ==================== Form Reset ====================
  onResetAllParkerProfileData(): void {
    this.parkerNewParkerForm.patchValue({
      isActive: '',
      companyId: '',
      lastName: '',
      firstName: '',
      primaryEmailAddress: '',
      startDate: '',
      endDate: '',
      cardNumber: '',
      stallNumber: '',
      rateNumber: '',
      isTaxExempt: '',
      parkerPayType: '',
      invoiceDeliveryMethod: '',
      billingAddress1: '',
      billingAddressLine: '',
      billingCity: '',
      billingState: '',
      billingZipCode: '',
      vehicleModel: '',
      vehicleMake: '',
      vehicleColor: '',
      vehicleState: '',
      vehicleLicense: '',
      comments: '',
      phone: '',
      description: 'Select rate to view description',
      stallType: 'Select rate to view stall type',
    });

    this.parkerNewParkerForm = new FormGroup({
      isActive: new FormControl('', []),
      companyId: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      firstName: new FormControl('', [Validators.required]),
      primaryEmailAddress: new FormControl('', []),
      startDate: new FormControl('', [Validators.required]),
      endDate: new FormControl('', []),
      cardNumber: new FormControl('', []),
      stallNumber: new FormControl('', []),
      rateNumber: new FormControl('', [Validators.required]),
      isTaxExempt: new FormControl('', []),
      parkerPayType: new FormControl('', [Validators.required]),
      invoiceDeliveryMethod: new FormControl('', [Validators.required]),
      billingAddress1: new FormControl('', []),
      billingAddressLine: new FormControl('', []),
      billingCity: new FormControl('', []),
      billingState: new FormControl('', []),
      billingZipCode: new FormControl('', []),
      vehicleModel: new FormControl('', []),
      vehicleMake: new FormControl('', []),
      vehicleColor: new FormControl('', []),
      vehicleState: new FormControl('', []),
      vehicleLicense: new FormControl('', []),
      comments: new FormControl('', []),
      phone: new FormControl('', []),
      description: new FormControl('Select rate to view description', []),
      stallType: new FormControl('Select rate to view stall type', []),
    });
  }

  // ==================== Rate / Company Change Handlers ====================
  onRateTypeChange(): void {
    let rateNumber: number = 0;
    let rateData: ILotRateList;
    let taxExemptFlag: string = '';

    rateNumber = +this.parkerNewParkerForm.get('rateNumber')!.value;

    if (rateNumber <= 0) {
      this.parkerNewParkerForm.patchValue({
        description: 'Select rate to view stall type',
        stallType: 'Select rate to view description',
        isTaxExempt: 'Select rate',
      });
    } else {
      let aStallType: IStallTypes;
      let rateData: ILotRateList;

      aStallType = this.stallDescriptions.find((x) => x.rateNumber == rateNumber)!;

      rateData = this.marketRateList.find((x) => x.rateNumber == rateNumber)!;

      //Set Tax Exempt
      if (rateData.rateType == 'M') taxExemptFlag = 'N';
      else {
        if (rateData.taxExempt == true) taxExemptFlag = 'Y';
        else taxExemptFlag = 'N';
      }

      this.setRateDescriptionStallTypeValues(aStallType.parkingStallType, aStallType.description, taxExemptFlag);

      if (aStallType.taxExempt === 'Y') {
        this.rateDescriptionSummary = `${aStallType.rateType}  ${rateData.displayRate} - ${rateData.description}`;
      } else {
        this.rateDescriptionSummary = `${aStallType.rateType}  ${rateData.displayRate} - ${rateData.description}`;
      }
    }
  }

  onCompanyNumberChange(): void {
    let companyId: string = '';

    this.marketRateList = [];
    this.labelSelectRate = 'Finding Rates...';

    companyId = this.parkerNewParkerForm.get('companyId')!.value;

    this.displaySearchingDialog();

    this.currentCompanyId = companyId;

    if (companyId === '') {
      this.marketRateList = [];
      this.parkerNewParkerForm.patchValue({
        rateNumber: '',
        isTaxExempt: 'Select Rate',
      });

      this.getMarketRateListByLotNumber(this._appGeneralState.currentLotNumber);
    } else {
      this.getCompanyAndMarketRateListByLotNumber(this._appGeneralState.currentLotNumber, companyId);
    }
  }

  displaySearchingDialog(): void {
    const dlgTitle: string = '';
    const dlgLine1: string = '';
    const dlgLine2: string = '';
    let enterAnimationDuration: string = '0ms';
    let exitAnimationDuration: string = '0ms';

    const dialogRef = this._dialog.open(DialogSearchingComponent, {
      data: { dlgTitle: dlgTitle, dlgLine1: dlgLine1, dlgLine2: dlgLine2 },
      width: '450px',
      enterAnimationDuration,
      exitAnimationDuration,
    });
  }

  setRateDescriptionStallTypeValues(stallType: string, rateDescription: string, taxExempt: string): void {
    let taxExemptDisplayValue: string = 'Yes';

    if (taxExempt == 'N') taxExemptDisplayValue = 'No';

    this.parkerNewParkerForm.patchValue({
      description: rateDescription,
      stallType: stallType,
      isTaxExempt: taxExemptDisplayValue,
    });
  }

  // ==================== Input Validators (Keypress / Paste) ====================
  keyPressAlphanumeric(event: any): boolean {
    var inp = String.fromCharCode(event.keyCode);

    if (/[a-zA-Z0-9]/.test(inp)) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  keyPressCardNumber(event: any): boolean {
    var inp = String.fromCharCode(event.keyCode);

    if (/[a-zA-Z0-9\-\._:]/.test(inp)) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  keyPressNumeric(event: any): boolean {
    var inp = String.fromCharCode(event.keyCode);

    if (/[0-9]/.test(inp)) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  validatePaste(e: any): boolean {
    const copiedText = e.clipboardData.getData('text');
    if (/[a-zA-Z0-9]/.test(copiedText) == false) {
      e.preventDefault(); //this line executes if copiedText has any invalid characters
      return false;
    } else return true;
  }

  // ==================== Parking Agreement Dialog & Rules ====================
  openGetExemptionCommentsDialog(): void {
    const enterAnimationDuration: string = '0ms';
    const exitAnimationDuration: string = '0ms';
    let exemptionComments: string = '';

    const dialogRef = this._dialog.open(DialogExemptionCommentsComponent, {
      data: {},
      width: '750px',
      enterAnimationDuration,
      exitAnimationDuration,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result == false) {
        this.parkingAgreementForm.setValue(
          {
            sendNotification: false,
            sendRequestForData: false,
            exemptFromAgreement: false,
            includeSpecialInstructionsLink: false,
          },
          { onlySelf: true, emitEvent: false },
        );
      } else {
        this.commentsForExemptAgreement = result;
      }
    });
  }

  initWatchParkingAgreementCheckboxes(): void {
    this.parkingAgreementForm.get('sendNotification')!.valueChanges.forEach((value) => {
      this.evalSendNotification(value);
    });

    this.parkingAgreementForm.get('sendRequestForData')!.valueChanges.forEach((value) => {
      this.evalSendRequestDataForm(value);
    });

    this.parkingAgreementForm.get('exemptFromAgreement')!.valueChanges.forEach((value) => {
      this.evalAgreementExempt(value);
    });

    this.parkingAgreementForm.get('includeSpecialInstructionsLink')!.valueChanges.forEach((value) => {
      this.evalincludeSpecialInstructionsLink(value);
    });
  }

  evalSendNotification(sendNotificationVal: any): void {
    if (sendNotificationVal) {
      this.parkingAgreementForm.setValue(
        {
          sendNotification: true,
          sendRequestForData: false,
          exemptFromAgreement: false,
          includeSpecialInstructionsLink: false,
        },
        { onlySelf: true, emitEvent: false },
      );
    } else {
      this.parkingAgreementForm.setValue(
        {
          sendNotification: false,
          sendRequestForData: false,
          exemptFromAgreement: false,
          includeSpecialInstructionsLink: false,
        },
        { onlySelf: true, emitEvent: false },
      );
    }
  }

  evalSendRequestDataForm(sendRequestFormVal: any): void {
    if (sendRequestFormVal) {
      this.parkingAgreementForm.setValue(
        {
          sendNotification: true,
          sendRequestForData: true,
          exemptFromAgreement: false,
          includeSpecialInstructionsLink: false,
        },
        { onlySelf: true, emitEvent: false },
      );
    } else {
    }
  }

  evalincludeSpecialInstructionsLink(sendRequestFormVal: any): void {
    if (sendRequestFormVal) {
      this.parkingAgreementForm.setValue(
        {
          sendNotification: true,
          sendRequestForData: true,
          exemptFromAgreement: false,
          includeSpecialInstructionsLink: true,
        },
        { onlySelf: true, emitEvent: false },
      );
    } else {
    }
  }

  evalAgreementExempt(agreementExemptVal: any): void {
    if (agreementExemptVal) {
      this.parkingAgreementForm.setValue(
        {
          sendNotification: false,
          sendRequestForData: false,
          exemptFromAgreement: true,
          includeSpecialInstructionsLink: false,
        },
        { onlySelf: true, emitEvent: false },
      );

      this.openGetExemptionCommentsDialog();
    } // the Exempt Agreement Checkbox has changed and set to False.
    else {
      //This requires removing any exisitng comment and telling the user that the comment was deleted
      this.commentsForExemptAgreement = '';

      this.matSnackBar.open('Any existing comments entered for the Parking Agreement Exemption have been deleted', '', {
        duration: 3000,
      });
    }
  }

  // ==================== Amano One Async Data Helpers ====================
  // This function's only job is to get the ID and return it.
  // Note the return type: Promise<string | null>
  async getAmanoOneAccessHolderId(lotNumber: string, cardNumber: string): Promise<string | null> {
    try {
      const accessHolderId = await firstValueFrom(this.dataService.getAmanoOneParcsCardNumber(lotNumber, cardNumber));

      // Return the result from the API call
      return accessHolderId;
    } catch (err) {
      // On error, return null so the calling function can handle it
      return null;
    }
  }

  async getAmanoOneAccessHolderTrackingData(
    lotNumber: string,
    cardNumber: string,
  ): Promise<IAmanoOneAccessHolderUpdateResponse | null> {
    let accessHolder: IAmanoOneAccessHolderUpdateResponse | null;

    try {
      accessHolder = await firstValueFrom(this.dataService.getAmanoOneParcsTrackingAccessHolder(lotNumber, cardNumber));
    } catch (err) {
      accessHolder = null;
    }
    return accessHolder !== null ? accessHolder : null;
  }

  onPasteAlphanumeric(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    // Remove all non-alphanumeric characters
    const sanitizedText = pastedText.replace(/[^a-zA-Z0-9]/g, '');
    // Update the form control with sanitized value
    this.parkerNewParkerForm.patchValue({
      vehicleLicense: sanitizedText.substring(0, 16), // Respect maxlength
    });
  }

  /// Gets the accessHolderId for a given lot number and card number.
  // This is used to check if an AccessHolder profile already exists for the card number being added to the Parker profile.
  async getAmanoOneAccessHolderForCardNumberLotNumber(lotNumber: string, cardNumber: string): Promise<string> {
    try {
      return await firstValueFrom(
        this.dataService.getAmanoOneAccessHolderforCardNumberLotNumber(lotNumber, cardNumber),
      );
    } catch (err) {
      return '';
    }
  }

  async getAmanoOneParkerDataForCardReassignment(
    accessHolderId: string,
  ): Promise<IAmanoParkerDataForCardReassignmentResponse | null> {
    try {
      return await firstValueFrom(this.dataService.getAmanoOneParkerDataForCardReassignment(accessHolderId));
    } catch (err) {
      return null;
    }
  }

  // ==================== Card Reassignment Flow ====================

  onParkerAddMultiple_ex(): void {
    this.onParkerAdd_ex(true);
  }

  /// saveAndAddAnother is passed in rather than set by the caller, because the flag survives a save
  /// and a plain Submit afterwards would otherwise still be treated as "add another".
  async onParkerAdd_ex(saveAndAddAnother: boolean = false): Promise<void> {
    const noDuplicateCardNumber: number = 0;
    const duplicateFoundAbort: number = 1;
    const duplicateFoundProceed: number = 2;

    // Guard against duplicate submissions from multiple/rapid clicks on any of the
    // Submit buttons. This must run synchronously (before any await) so the buttons
    // are disabled immediately on the first click, otherwise a second click during
    // the awaited validation/card-reassignment calls would create a duplicate parker.
    if (this.disableSaveButton) {
      return;
    }
    this.disableSaveButton = true;
    this.saveAndAddNewParkerFlag = saveAndAddAnother;

    // Show the processing banner immediately - before any awaited validation or Amano
    // card-lookup calls - so it paints on the next change-detection tick instead of
    // waiting for those network round-trips to resolve.
    this.processingMessage = 'Saving New Parker - Stand by...';
    this.isProcessingSave = true;

    try {
      this.onParkerAddNewInit_ex();
      if (false == (await this.addNewParker_Validation_ex())) {
        throw new Error('Validation failed.');
      }

      // //Is this a PARCS parker, or non PARCS.
      // //If non PARCS, check cardNumber against other cardNumbers in the Parker table.
      // if (!this.hasParc) {
      //   console.log('Non-PARCS parker; checking for duplicate card number in Parker table...');
      //   return;
      // } else {
      //   console.log('PARCS parker; checking for duplicate card number in Amano One...');
      // }

      // return;

      // Only check this if the parker is a PARCS/Amano One parker and we are saving to Amano.
      if (this.hasParc && this.isParcsAmanoOne && this.parcsSaveButtonDisabled == false) {
        if (duplicateFoundAbort === (await this.reassignAmanoOneCardNumber())) {
          this.isProcessingSave = false; // No save will occur; hide the processing banner
          this.disableSaveButton = false; // Re-enable so the user can correct and resubmit
          return; // A duplicate cardNumber was found, but user aborted adding this parker
        }
      }

      if(this.hasParc && this.isParcsFlash) {
        //placeholder for handling PARCS Flash parker logic before saving

      }


      // Proceed with saving the parker profile, either because no duplicate was found or user chose to proceed with reassignment
      this.saveParkerProfile_ex();
    } catch (err) {
      console.error('Error during validation: ', err);
      this.isProcessingSave = false; // Validation failed / aborted; hide the processing banner
      this.disableSaveButton = false; // Re-enable so the user can correct and resubmit
    }

    // If this is a PARCS/Amano One
    //else case
  }

  // Initialization function for adding a new parker.
  // Init vars and any other setup/start
  onParkerAddNewInit_ex(): void {
    this.isCardReassignmentPending = false;
  }

  // EMail/Parker Agreement Checked

  // Returns true if the user confirmed the card number reassignment, false otherwise.
  async displayDialogCardNumberReassign(
    amanoParkerData: IAmanoParkerDataForCardReassignmentResponse,
  ): Promise<boolean> {
    let parkerName: string = amanoParkerData.lastName + ', ' + amanoParkerData.firstName;
    // Use the card number entered on the form (the value passed to the lookup sprocs); the API response
    // omits cardNumber when the Amano access holder has no associated spACE-AR parker.
    let cardNumber: string = this.parkerNewParkerForm.get('cardNumber')!.value;
    let parkerId: string = amanoParkerData.parkerId;
    // Guard against a null/undefined status so a missing 'active' value is treated as inactive instead of throwing.
    let isActive: boolean = (amanoParkerData.active ?? '').toUpperCase() == 'ACTIVE' ? true : false;

    const dialogRef = this._dialog.open(DialogCardNumberReassignComponent, {
      width: '650px',
      data: {
        parkerName,
        cardNumber,
        parkerId,
        isActive,
      },
    });

    const result: boolean = (await firstValueFrom(dialogRef.afterClosed())) === true;
    console.log(result ? 'User confirmed card reassignment.' : 'User canceled card reassignment.');

    return result;
  }

  async reassignAmanoOneCardNumber(): Promise<number> {
    let returnValue: number = 0;
    const noDuplicateCardNumber: number = 0;
    const duplicateFoundAbort: number = 1;
    const duplicateFoundProceed: number = 2;
    let reassignCardNumber: boolean = false;
    let cardNumber: string = this.parkerNewParkerForm.get('cardNumber')!.value;
    const accessHolderId: string = await this.getAmanoOneAccessHolderForCardNumberLotNumber(
      this._appGeneralState.currentLotNumber,
      cardNumber,
    );
    if (accessHolderId != '') {
      const amanoParkerData: IAmanoParkerDataForCardReassignmentResponse | null =
        await this.getAmanoOneParkerDataForCardReassignment(accessHolderId);
      if (amanoParkerData) {
        this.isProcessingSave = false; // Hide the processing banner while awaiting the user's reassignment decision
        if (await this.displayDialogCardNumberReassign(amanoParkerData)) {
          this.isProcessingSave = true; // User chose to proceed; restore the banner through save
          this.isCardReassignmentPending = true;
          returnValue = duplicateFoundProceed;
        } else {
          this.isCardReassignmentPending = false;
          returnValue = duplicateFoundAbort;
        }
      }
    }
    return returnValue;
  }

  async addNewParker_Validation_ex(): Promise<boolean> {
    let isEmailParkingAgreementValid: boolean = true;

    if (!this.isEndDateWithinMaxValidated()) {
      return false;
    }

    isEmailParkingAgreementValid = await this.isParkingAgreementEmailAddressValidated_ex();

    return isEmailParkingAgreementValid;
  }

  /// Last line of defence for the End Date ceiling - the datepicker can be bypassed by typing.
  isEndDateWithinMaxValidated(): boolean {
    if (!isParkerEndDateBeyondMax(this.parkerNewParkerForm.get('endDate')!.value)) {
      return true;
    }

    this.parkerNewParkerForm.controls['endDate'].setErrors({
      ...(this.parkerNewParkerForm.controls['endDate'].errors ?? {}),
      maxEndDate: true,
    });

    this._messageDialogService.displayGenericErrorDialog(
      'Invalid End Date',
      `The end date cannot be later than ${this.maxEndDateDisplay}.`,
      'Correct the end date and submit again.',
    );

    return false;
  }

  // Returns true if Validation Passes.
  // If Parking Agreement checked, then there must be an email address.
  // True = Passed
  async isParkingAgreementEmailAddressValidated_ex(): Promise<boolean> {
    let status: boolean = true;
    const enterAnimationDuration: string = '0ms';
    const exitAnimationDuration: string = '0ms';
    const agreementChecked: boolean = Boolean(this.parkingAgreementForm.get('sendNotification')!.value);
    const emailAddress: boolean = Boolean(this.parkerNewParkerForm.get('primaryEmailAddress')!.value);

    if (agreementChecked && !emailAddress) {
      status = false;
      const dialogRef = this._dialog.open(DialogConfirmSaveParkerComponent, {
        data: { isAgreementChecked: agreementChecked },
        width: '450px',
        enterAnimationDuration,
        exitAnimationDuration,
      });

      await firstValueFrom(dialogRef.afterClosed());
    }

    return status;
  }

  setAddParkerModel_ex(): any {
    let newParkerData: IParkerProfileDetail = {} as IParkerProfileDetail;
    let endDateNotSetFlag: Date = new Date('1900-01-01');
    let parkerHelper = new ParkerImportPrep(this.companyList);
    let parkerAgreementData: IParkingAgreementNewParkingAgreementLink = {} as IParkingAgreementNewParkingAgreementLink;

    this.newParkerId = parkerHelper.CreateNewParkerId();
    newParkerData.parkerId = this.newParkerId;
    newParkerData.lotNumber = this._appGeneralState.currentLotNumber;
    newParkerData.companyId = this.parkerNewParkerForm.get('companyId')!.value;
    newParkerData.lastName = this.parkerNewParkerForm.get('lastName')!.value;
    newParkerData.firstName = this.parkerNewParkerForm.get('firstName')!.value;
    newParkerData.address = this.parkerNewParkerForm.get('billingAddress1')!.value;
    newParkerData.addressLine = this.parkerNewParkerForm.get('billingAddressLine')!.value;
    newParkerData.city = this.parkerNewParkerForm.get('billingCity')!.value;
    newParkerData.state = this.parkerNewParkerForm.get('billingState')!.value;
    newParkerData.zipCode = this.parkerNewParkerForm.get('billingZipCode')!.value;
    newParkerData.phone = this.parkerNewParkerForm.get('phone')!.value;
    newParkerData.emailAddress = this.parkerNewParkerForm.get('primaryEmailAddress')!.value;
    newParkerData.activeAsBoolean = true;
    newParkerData.cardNumber = this.parkerNewParkerForm.get('cardNumber')!.value;
    newParkerData.vehicleColor = this.parkerNewParkerForm.get('vehicleColor')!.value;
    newParkerData.vehicleMake = this.parkerNewParkerForm.get('vehicleMake')!.value;
    newParkerData.vehicleModel = this.parkerNewParkerForm.get('vehicleModel')!.value;
    newParkerData.vehicleLicense = this.parkerNewParkerForm.get('vehicleLicense')!.value;
    newParkerData.vehicleState = this.parkerNewParkerForm.get('vehicleState')!.value;
    newParkerData.payType = 'todo - get pay type';
    newParkerData.rateNumber = +this.parkerNewParkerForm.get('rateNumber')!.value;
    newParkerData.deliveryType = this.parkerNewParkerForm.get('invoiceDeliveryMethod')!.value;
    newParkerData.parkerPayType = this.parkerNewParkerForm.get('parkerPayType')!.value;
    newParkerData.startDate = this.parkerNewParkerForm.get('startDate')!.value;
    newParkerData.endDate = this.parkerNewParkerForm.get('endDate')!.value;
    newParkerData.userName = this._appGeneralState.userName;
    newParkerData.stallNumber = this.parkerNewParkerForm.get('stallNumber')!.value;
    newParkerData.isTermDateSet = true;
    newParkerData.comments = this.parkerNewParkerForm.get('comments')!.value;
    newParkerData.group = '';
    newParkerData.active = '';
    newParkerData.company = '';
    newParkerData.stallType = this.parkerNewParkerForm.get('stallType')!.value;
    newParkerData.companyName = '';
    newParkerData.paymentType = '';
    newParkerData.rateDisplay = '';
    //newParkerData.addressLine = '';
    newParkerData.endDateDisplay = '';
    newParkerData.rateDescription = '';
    newParkerData.additionalEmails = 0;
    newParkerData.companyStartDate = '';
    newParkerData.standardRateType = '';
    newParkerData.startDateDisplay = '';
    newParkerData.additionalVehicles = 0;
    newParkerData.rateNumberAsString = '';
    newParkerData.additionalCardCount = '';
    newParkerData.additionalEmailCount = '';
    newParkerData.rateDescriptionSummary = '';
    newParkerData.rateType = '';
    newParkerData.spaceEmailAddress = '';
    newParkerData.spaceEmailId = 0;
    newParkerData.lotName = '';
    newParkerData.parkingAgreementNotification = parkerAgreementData;

    if (newParkerData.parkerPayType.toUpperCase() === 'PARKER') newParkerData.tenantId = this.newParkerId;
    else newParkerData.tenantId = newParkerData.companyId;

    //If the user didn't select an end date, use this as a flag with the endpoint
    if (
      this.parkerNewParkerForm.get('endDate')!.value === '' ||
      this.parkerNewParkerForm.get('endDate')!.value === null
    )
      newParkerData.endDate = endDateNotSetFlag;

    // 20240809 :: Ken Moss.  Fixed below.  If statement was incorrect and not setting tax exempt flag correctly
    // 20241007 :: Ken Moss.  Removed the else portion of this if statement, which was incorrect.  The else was setting the taxExempt = true
    const taxExemptFlag: string = this.parkerNewParkerForm.get('isTaxExempt')!.value;
    if (taxExemptFlag === '') {
      newParkerData.taxExempt = false;
    } else {
      newParkerData.taxExempt = taxExemptFlag.toUpperCase() == 'YES' ? true : false;
    }

    if (this.parkingAgreementForm.get('sendNotification')!.value) {
      parkerAgreementData.includeDataRequest = this.parkingAgreementForm.get('sendRequestForData')!.value;
      parkerAgreementData.parkerId = this.newParkerId;
      parkerAgreementData.lotNumber = this._appGeneralState.currentLotNumber;
      parkerAgreementData.userName = this._appGeneralState.userName;
      parkerAgreementData.includeSpecialInstructions = this.parkingAgreementForm.get(
        'includeSpecialInstructionsLink',
      )!.value;
      parkerAgreementData.isExempt = false;
      parkerAgreementData.exemptComments = '';
      parkerAgreementData.isResendNotification = false;
      parkerAgreementData.isNewAgreement = true;

      // 20241011 :: Ken Moss.  The email address was added to the parkerAgreementData model
      parkerAgreementData.recipientEmailAddress = this.parkerNewParkerForm.get('primaryEmailAddress')!.value;
      newParkerData.parkingAgreementNotification = parkerAgreementData;
    }

    if (this.parkingAgreementForm.get('exemptFromAgreement')!.value == true) {
      parkerAgreementData.isExempt = true;
      parkerAgreementData.parkerId = this.newParkerId;
      parkerAgreementData.exemptComments = this.commentsForExemptAgreement;
      parkerAgreementData.isNewAgreement = false;

      newParkerData.parkingAgreementNotification = parkerAgreementData;
    }

    return newParkerData;
  }

  async saveParkerProfile_ex(): Promise<void> {
    let newParkerData: any = this.setAddParkerModel();

    /// Determine if in add multiple parker mode
    /// Prep if we want to add the most recent parker to the recently added parker list
    if (this.saveAndAddNewParkerFlag) {
      this.addMostRecentSavedParkerToRecentParkerList_ex(newParkerData);
    }

    this.parkerStateService.activeParkerId = this.newParkerId;

    if (this.hasParc && this.isParcsAmanoOne && this.parcsSaveButtonDisabled == false && this.amanoOneAccessGroupId != '') {
      this.disableSaveButton = true;
      this.postNewParkerProfileAmanoOne(newParkerData);

      /* * * * * * * * *
      * 20260618 :: Ken Moss
      * Removed but left in via commented out in the event we want to reinstate this logic.
      * * * * * * * * * *
      // if PARCS enabled and we are adding an AccessHolder profile
      // PARCS Path
      if (newParkerData.cardNumber && newParkerData.cardNumber != '') {
        const accessHolderId = await this.getAmanoOneAccessHolderId(newParkerData.lotNumber, newParkerData.cardNumber);
        this.evalIfAmanoOneParcsCardNumberExists(newParkerData, accessHolderId);
      } else {
        this.disableSaveButton = true;
        this.postNewParkerProfileAmanoOne(newParkerData);
      }

      = = = = = = = = = = 
      = 20260618 :: Ken Moss
      = Removed but left in via commented out in the event we want to reinstate this logic.
      = = = = = = = = = */
    } else if (this.hasParc && this.isParcsFlash && this.parcsSaveButtonDisabled == false) {
      this.disableSaveButton = true;
      this.postNewParkerProfileFlash(newParkerData);
    } else if (this.hasParc && this.isParcsAmanoOne && this.parcsSaveButtonDisabled == false && this.amanoOneAccessGroupId == '') {
      this.postNewParkerProfile(newParkerData);

      // 20260715 :: Ken Moss -- Marked for Deletion
      // Removed the code below.  It checks to see if a cardNumber is alrady in use.
      // This code is redundant as the cardNumber duplicate is already checked with new functionality.
      // TODO: Safe to remove after 20160901

      // if PARCS enabled and we are NOTadding an AccessHolder profile
      // const accessHolderId = await this.getAmanoOneAccessHolderId(newParkerData.lotNumber, newParkerData.cardNumber);
      // if (accessHolderId != null && accessHolderId != '') {
      //   let accessHolder: IAmanoOneAccessHolderUpdateResponse | null = await this.getAmanoOneAccessHolderTrackingData(
      //     newParkerData.lotNumber,
      //     newParkerData.cardNumber,
      //   );
      //   this.isProcessingSave = false; // No save will occur here; hide the processing banner before showing the dialog
      //   this.displayAmanoParcsExistsDialogNotAddingAccessHolder(newParkerData, accessHolder);
      // } else {
      //   this.disableSaveButton = true;
      //   this.postNewParkerProfile(newParkerData);
      // }
    } else {
      // No PARCS Path
      this.disableSaveButton = true;
      this.postNewParkerProfile(newParkerData);
      console.log('No PARCS Path');
    }
  }

  addMostRecentSavedParkerToRecentParkerList_ex(newlyAddedParker: IParkerProfileDetail): void {
    let newParker: IRecentlyAddedParkerProfile = {} as IRecentlyAddedParkerProfile;

    let ii: number;
    let companyName: string = '';

    for (ii = 0; ii < this.companyList.length; ii++) {
      if (this.companyList[ii].companyId === newlyAddedParker.companyId) companyName = this.companyList[ii].companyName;
    }

    newParker.emailAddress = newlyAddedParker.emailAddress;
    newParker.companyName = companyName;
    newParker.firstName = newlyAddedParker.firstName;
    newParker.lastName = newlyAddedParker.lastName;
    newParker.startDate = newlyAddedParker.startDate;
    newParker.endDate = newlyAddedParker.endDate;
    newParker.parkerPayType = newlyAddedParker.parkerPayType;
    newParker.invoiceDeliveryMethod = newlyAddedParker.deliveryType;

    this.recentlyAddedParkers.push(newParker);
  }
} //END END END

// 20260628 :: Ken Moss
// Business Rules for Adding New Parker

// Form Validation

// Adding New Parkers

// Adding New Multiple Parkers
